/**
 * DeviceClusterMarker — represents N devices whose individual markers
 * would visually overlap on the map (centres within ~28 px at the
 * current zoom). Renders a single disc with a count badge instead of
 * stacked single markers; clicking it opens a popup that lists every
 * device at that spot, each row linking to the device detail page.
 *
 * Visual language mirrors DeviceMapMarker so the user reads "cluster"
 * and "single" as the same vocabulary, different cardinality:
 *  - same 24 px disc, drop shadow, hairline edge
 *  - env-group ring colour only when ALL members share a group;
 *    otherwise FALLBACK_RING (don't imply uniformity that isn't there)
 *  - inner status fill grown to r=7 (vs r=5 single) so a digit fits;
 *    colour is the dominant status (any online → online; else any
 *    stale → stale; else offline)
 *  - centred count in white, Inter, 700 weight; "9+" for ≥10 so the
 *    digit never overflows the disc
 *  - no glass-bead highlight; the count IS the inner content
 */
import { useEffect, useMemo, useRef } from 'react';
import { divIcon } from 'leaflet';
import type { DivIcon, Marker as LeafletMarker } from 'leaflet';
import { Marker, Popup } from 'react-leaflet';
import { Link } from 'react-router-dom';
import {
  ENV_GROUP_LABEL,
  type DeviceSummary,
  type EnvGroup,
} from '../api/hooks';
import { fmtAgo } from './format';
import {
  FALLBACK_RING,
  GROUP_RING,
  STATUS_FILL,
  compareForSidebar,
  deviceStatus,
  type MarkerStatus,
} from './DeviceMapMarker';

/** Dominant status across the cluster — same precedence the popup header uses. */
function dominantStatus(devices: DeviceSummary[]): MarkerStatus {
  let sawStale = false;
  for (const d of devices) {
    const s = deviceStatus(d);
    if (s === 'online') return 'online';
    if (s === 'stale') sawStale = true;
  }
  return sawStale ? 'stale' : 'offline';
}

/** Returns the shared group if every device has it; otherwise null. */
function sharedGroup(
  devices: DeviceSummary[],
  groupOf: Map<string, EnvGroup>,
): EnvGroup | null {
  const first = groupOf.get(devices[0]!.thing) ?? null;
  if (first === null) return null;
  for (let i = 1; i < devices.length; i++) {
    if (groupOf.get(devices[i]!.thing) !== first) return null;
  }
  return first;
}

/** Mean of constituent lat/lon — used as the marker anchor + fly-to target.
 * Callers pre-filter to devices with valid coords; the non-null
 * assertions are safe at runtime (DE-162 widened the type to nullable). */
function centroid(devices: DeviceSummary[]): [number, number] {
  let lat = 0;
  let lon = 0;
  for (const d of devices) {
    lat += d.geo!.lat!;
    lon += d.geo!.lon!;
  }
  return [lat / devices.length, lon / devices.length];
}

function buildClusterIcon(
  count: number,
  status: MarkerStatus,
  group: EnvGroup | null,
): DivIcon {
  const fill = STATUS_FILL[status];
  const ring = group ? GROUP_RING[group] : FALLBACK_RING;
  const label = count >= 10 ? '9+' : String(count);
  // Filter IDs scoped per (status, group, label) so multiple inline
  // SVGs in the same document don't collide on the global id lookup.
  const variantId = `${status}-${group ?? 'none'}-${label}`;
  const html = `
    <svg viewBox="0 0 24 24" width="24" height="24" overflow="visible"
         xmlns="http://www.w3.org/2000/svg"
         aria-hidden="true" focusable="false">
      <defs>
        <filter id="c-ds-${variantId}" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="0.8"
                        flood-color="#1C1B1F" flood-opacity="0.28"/>
        </filter>
      </defs>
      <g filter="url(#c-ds-${variantId})">
        <circle cx="12" cy="12" r="10.5" fill="#ffffff"/>
        <circle cx="12" cy="12" r="10.5" fill="none"
                stroke="#1C1B1F" stroke-opacity="0.15" stroke-width="0.5"/>
        <circle cx="12" cy="12" r="9" fill="none"
                stroke="${ring}" stroke-width="2"/>
        <circle cx="12" cy="12" r="7" fill="${fill}"/>
        <text x="12" y="12.4"
              text-anchor="middle" dominant-baseline="middle"
              font-family="Nunito Sans, system-ui, sans-serif"
              font-size="9" font-weight="800"
              fill="#ffffff">${label}</text>
      </g>
    </svg>
  `;
  return divIcon({
    html,
    className: 'artomeui-device-cluster',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
}

interface DeviceClusterMarkerProps {
  devices: DeviceSummary[];
  groupOf: Map<string, EnvGroup>;
  /**
   * Register-self callback. The cluster marker registers under EVERY
   * member device's thing key, so the sidebar's
   * `markerRefs.current[thing]?.openPopup()` lookup still finds a
   * marker for any device in scope — it just happens to be the cluster
   * popup, which lists the clicked device alongside its bunkmates.
   */
  registerRef?: (thing: string, marker: LeafletMarker | null) => void;
}

export function DeviceClusterMarker({
  devices,
  groupOf,
  registerRef,
}: DeviceClusterMarkerProps) {
  const status = dominantStatus(devices);
  const group = sharedGroup(devices, groupOf);
  const icon = useMemo(
    () => buildClusterIcon(devices.length, status, group),
    [devices.length, status, group],
  );
  const position = useMemo(() => centroid(devices), [devices]);

  // Register under every member thing so sidebar-click lookups land
  // on this cluster's popup. Re-runs when the cluster's membership
  // changes (caller re-keys the component, which remounts).
  const markerRef = useRef<LeafletMarker | null>(null);
  useEffect(() => {
    if (!registerRef) return;
    const things = devices.map((d) => d.thing);
    const m = markerRef.current;
    for (const t of things) registerRef(t, m);
    return () => {
      for (const t of things) registerRef(t, null);
    };
  }, [devices, registerRef]);

  // Use the first device's accuracy as a representative figure — for
  // tightly-clustered devices (same AP set) this is bit-identical
  // across members. Loose clusters (near-coords) may have varied
  // accuracies; one number is still useful and avoids list bloat.
  const repAccuracy = devices[0]!.geo!.accuracy;

  // Sorted copy for the popup list — keeps the order stable across
  // 30 s polls (only online↔offline transitions reshuffle).
  const sortedDevices = [...devices].sort(compareForSidebar);

  return (
    <Marker
      position={position}
      icon={icon}
      ref={(m) => {
        markerRef.current = m;
      }}
      title={`${devices.length} devices at this location`}
    >
      <Popup maxWidth={360} minWidth={260}>
        <div className="text-xs space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-medium text-fg">
              {devices.length} devices at this location
            </span>
          </div>
          <div className="text-muted tabular-nums">
            {position[0].toFixed(5)}, {position[1].toFixed(5)}
            {repAccuracy ? ` ±${Math.round(repAccuracy)} m` : ''}
          </div>
          <ul className="space-y-0.5 max-h-72 overflow-y-auto -mx-1.5 pt-1 border-t border-border">
            {sortedDevices.map((d) => {
              const g = groupOf.get(d.thing) ?? null;
              const s = deviceStatus(d);
              const dotClass =
                s === 'online'
                  ? 'bg-accent'
                  : s === 'stale'
                    ? 'bg-warn'
                    : 'bg-muted';
              return (
                <li key={d.thing}>
                  <Link
                    to={`/devices/${encodeURIComponent(d.thing)}`}
                    className="group/row flex items-center gap-2 px-1.5 py-1 rounded-md text-left w-full transition-colors hover:bg-soft focus:outline-none focus-visible:bg-soft focus-visible:ring-1 focus-visible:ring-accent"
                  >
                    <span
                      className={`inline-block w-2 h-2 rounded-full shrink-0 ring-1 ring-white/80 ${dotClass}`}
                      aria-label={s}
                    />
                    <span
                      className={
                        d.friendly_name
                          ? 'text-xs flex-1 min-w-0 truncate font-medium text-fg'
                          : 'text-xs flex-1 min-w-0 truncate font-mono text-fg'
                      }
                      title={d.thing}
                    >
                      {d.friendly_name ?? d.thing}
                    </span>
                    {g && (
                      <span className="pill-muted shrink-0">
                        {ENV_GROUP_LABEL[g]}
                      </span>
                    )}
                    <span className="text-[10px] text-muted tabular-nums tracking-wide shrink-0">
                      {fmtAgo(d.last_seen)}
                    </span>
                    <span
                      className="text-muted text-xs leading-none shrink-0 opacity-0 -mr-0.5 transition-opacity group-hover/row:opacity-70 group-focus-visible/row:opacity-100"
                      aria-hidden="true"
                    >
                      ›
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </Popup>
    </Marker>
  );
}
